/**
 * Centralized WebSocket utilities with auth handling
 */
import { logger } from '@/utils/logger';
import { BACKEND_CONFIG } from '@/config/backendConfig';

/**
 * Get the appropriate login URL based on user type
 */
export const getLoginUrl = (): string => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user.user_type === 'referral' || user.user_type === 'referral_team') {
        return '/referral-portal/login';
      }
    } catch (error) {
      // If parsing fails, default to staff login
    }
  }

  // Check current URL path to determine context
  const currentPath = window.location.pathname;
  if (currentPath.startsWith('/referral-portal')) {
    return '/referral-portal/login';
  }

  // Default to staff login
  return '/login';
};

/**
 * Handle WebSocket authentication errors
 * Clears tokens and redirects to appropriate login page
 */
export const handleWebSocketAuthError = (): void => {
  logger.warn('WebSocket authentication failed - clearing session and redirecting to login');
  console.log('[WebSocket Auth] Handling auth error - will clear session and redirect');

  // Clear session
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');

  // Redirect to login
  const loginUrl = getLoginUrl();
  console.log('[WebSocket Auth] Redirecting to:', loginUrl);
  window.location.href = loginUrl;
};

/**
 * Check if WebSocket close code indicates an auth error
 */
export const isAuthError = (code: number): boolean => {
  // Common WebSocket close codes for auth errors:
  // 1008 = Policy Violation (used for auth failures)
  // 4001 = Custom: Unauthorized
  // 4003 = Custom: Forbidden
  const isAuth = code === 1008 || code === 4001 || code === 4003;
  console.log('[WebSocket Auth] Checking close code:', code, 'isAuthError:', isAuth);
  return isAuth;
};

/**
 * Check if WebSocket close code indicates a fatal error that shouldn't retry
 */
export const isFatalError = (code: number): boolean => {
  // Fatal error codes that shouldn't trigger reconnection:
  // 1002 = Protocol Error
  // 1003 = Unsupported Data (e.g., wrong data type)
  // 1006 = Abnormal Closure (often 404 or network error)
  // 1008 = Policy Violation (auth errors)
  // 1011 = Internal Server Error (server-side issue)
  // 4001-4999 = Custom application errors
  const isFatal = code === 1002 || code === 1003 || code === 1006 ||
                  code === 1008 || code === 1011 || (code >= 4001 && code <= 4999);
  console.log('[WebSocket] Checking close code:', code, 'isFatalError:', isFatal);
  return isFatal;
};

/**
 * Get tenant-aware WebSocket URL
 */
export const getWebSocketUrl = (path: string, params?: Record<string, string>): string => {
  const token = localStorage.getItem('access_token');
  if (!token) {
    throw new Error('No access token available');
  }

  // Get tenant from session storage
  const userDataString = localStorage.getItem('user');
  let tenant = 'anandlab'; // Default
  if (userDataString) {
    try {
      const userData = JSON.parse(userDataString);
      tenant = userData.tenant_schema || userData.tenant || 'anandlab';
    } catch (e) {
      logger.warn('Failed to parse user data for tenant', e);
    }
  }

  // Build query params
  const queryParams = new URLSearchParams({
    token,
    tenant,
    ...params,
  });

  // Get WebSocket base URL from environment variable or construct for localhost
  let wsBaseUrl: string;
  const hostname = window.location.hostname;
  const apiPort = BACKEND_CONFIG.apiPort; // Use configured port (8000 for Django, 8001 for FastAPI)

  if (hostname.includes('.localhost')) {
    // For localhost with subdomains (e.g., anandlab.localhost)
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    wsBaseUrl = `${protocol}//${hostname}:${apiPort}/ws`;
  } else if (import.meta.env.VITE_WS_URL) {
    // For production, use environment variable
    wsBaseUrl = import.meta.env.VITE_WS_URL;
  } else {
    // Fallback (should not be used in production)
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    console.warn('⚠️ VITE_WS_URL not found! Falling back to constructed URL');
    wsBaseUrl = `${protocol}//${hostname}:${apiPort}/ws`;
  }

  return `${wsBaseUrl}${path}?${queryParams.toString()}`;
};

/**
 * Create WebSocket with automatic auth error handling
 */
export const createAuthenticatedWebSocket = (
  path: string,
  params?: Record<string, string>
): WebSocket | null => {
  try {
    const url = getWebSocketUrl(path, params);
    const ws = new WebSocket(url);

    // Add auth error handler
    const originalOnClose = ws.onclose;
    ws.onclose = (event) => {
      // Check if close was due to auth error
      if (isAuthError(event.code)) {
        handleWebSocketAuthError();
      }

      // Call original handler if exists
      if (originalOnClose) {
        originalOnClose.call(ws, event);
      }
    };

    return ws;
  } catch (error) {
    logger.error('Failed to create WebSocket:', error);
    return null;
  }
};
